/**
 * /execution/timeline — pick a job, render canonical ExecutionTimeline.
 * Read-only.
 */
import { useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useAppData } from "@/data/AppDataContext";
import { useRole } from "@/data/RoleContext";
import { canAccessExecutionDomain } from "@/data/executionDomain";
import { CockpitPage } from "@/components/cockpit/CockpitPage";
import { ExecutionTimeline } from "@/components/observability/ExecutionTimeline";

const COMPLETED = new Set(["Completed", "QA Review"]);

export default function ExecutionTimelinePage() {
  const { role } = useRole();
  const { jobs } = useAppData();
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<string | null>(null);

  const visibleJobs = useMemo(() => {
    const q = query.trim().toLowerCase();
    return jobs
      .filter((j) => !COMPLETED.has(j.currentStage) || q.length > 0)
      .filter(
        (j) =>
          q.length === 0 ||
          j.id.toLowerCase().includes(q) ||
          (j.customerName ?? "").toLowerCase().includes(q),
      )
      .slice(0, 50);
  }, [jobs, query]);

  if (!canAccessExecutionDomain(role)) return null;

  const activeJobId = selected ?? visibleJobs[0]?.id ?? null;

  return (
    <CockpitPage title="Execution Timeline" maxWidth="7xl">
      <div className="grid grid-cols-1 lg:grid-cols-[280px,1fr] gap-4">
        <Card className="p-3 max-h-[600px] overflow-y-auto">
          <Input
            placeholder="Search jobs…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="mb-2"
          />
          <div className="space-y-1">
            {visibleJobs.map((j) => {
              const active = activeJobId === j.id;
              return (
                <button
                  key={j.id}
                  onClick={() => setSelected(j.id)}
                  className={`w-full text-left px-2 py-2 rounded-md text-[12px] border ${
                    active
                      ? "bg-primary/15 text-primary ring-1 ring-primary/30 border-transparent"
                      : "border-transparent hover:bg-muted/40"
                  }`}
                >
                  <div className="font-semibold">{j.id}</div>
                  <div className="text-[11px] text-muted-foreground truncate">{j.customerName}</div>
                  <Badge variant="outline" className="text-[9px] mt-0.5">{j.currentStage}</Badge>
                </button>
              );
            })}
            {visibleJobs.length === 0 && (
              <div className="text-[12px] text-muted-foreground p-3 text-center">No jobs match.</div>
            )}
          </div>
        </Card>

        <div>
          {activeJobId ? (
            <ExecutionTimeline jobId={activeJobId} maxHeight={600} />
          ) : (
            <Card className="p-8 text-center text-sm text-muted-foreground">
              Select a job to view its execution timeline.
            </Card>
          )}
        </div>
      </div>
    </CockpitPage>
  );
}
